import { createContext, createElement, ReactNode, useContext, useEffect, useState } from 'react';
import { AnalyticsService } from '../services/analytics.service';

// Define types for the chart data
export interface SectionChartData {
  labels: string[];
  datasets: {
    correctAnswers: number[];
    incorrectAnswers: number[];
  };
}

export interface UserAnalyticsResponse {
  status: 'success' | 'error';
  data: UserAnalytics;
}

export interface UserAnalytics {
  sections?: AllSectionsChartData
  monthlyTotalPerformance?: MonthlyTotalPerformance[]
  processSummary?: ProcessPerformance[]
}

interface ProcessPerformance {
  processId: string;
  testName: string;
  testId: string;
  averageCorrect: number;
  averageIncorrect: number;
  totalTests: number;
  totalQuestions: number;
  completedAt: string;
}

interface MonthlyTotalPerformance {
  year: number;
  month: number;
  correctAnswers: number;
  incorrectAnswers: number;
}

export interface AllSectionsChartData {
  reading: SectionChartData;
  writing: SectionChartData;
  math: SectionChartData;
  readingAndWriting: SectionChartData;
  newMath: SectionChartData;
}

export interface SectionStats {
  totalCorrect: number;
  totalIncorrect: number;
  totalQuestions: number;
  correctPercentage: number;
}

export interface AllSectionsStats {
  reading: SectionStats;
  writing: SectionStats;
  math: SectionStats;
  readingAndWriting: SectionStats;
  newMath: SectionStats;
}

// Define the context type
interface AnalyticsContextType {
  loading: boolean;
  error: string | null;
  analyticsData: UserAnalytics | null;
  sectionStats: AllSectionsStats | null;
  refreshData: () => Promise<void>;
}

// Create a context for analytics data
const AnalyticsContext = createContext<AnalyticsContextType | null>(null);

/**
 * Custom hook to fetch and process analytics data
 */
const useAnalyticsDataFetcher = (): AnalyticsContextType => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [analyticsData, setChartData] = useState<UserAnalytics | null>(null);
  const [sectionStats, setSectionStats] = useState<AllSectionsStats | null>(null);

  /**
   * Fetch analytics data from the API
   */
  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);

      // Fetch all sections data using the service
      const { data } = await AnalyticsService.getAllSectionsChartData();

      setChartData(data);

      // Calculate statistics for each section
      const stats: AllSectionsStats = {
        reading: calculateSectionStats(data.sections?.reading),
        writing: calculateSectionStats(data.sections?.writing),
        math: calculateSectionStats(data.sections?.math),
        readingAndWriting: calculateSectionStats(data.sections?.readingAndWriting),
        newMath: calculateSectionStats(data.sections?.newMath)
      };

      setSectionStats(stats);
      setError(null);
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  /**
   * Calculate statistics for a section
   */
  const calculateSectionStats = (sectionData: SectionChartData): SectionStats => {
    // Calculate total correct answers
    const totalCorrect = sectionData.datasets.correctAnswers.reduce(
      (sum, current) => sum + current,
      0
    );

    // Calculate total incorrect answers
    const totalIncorrect = sectionData.datasets.incorrectAnswers.reduce(
      (sum, current) => sum + current,
      0
    );

    // Calculate total questions
    const totalQuestions = totalCorrect + totalIncorrect;

    // Calculate percentage
    const correctPercentage = totalQuestions > 0
      ? Math.round((totalCorrect / totalQuestions) * 100)
      : 0;

    return {
      totalCorrect,
      totalIncorrect,
      totalQuestions,
      correctPercentage
    };
  };

  return {
    loading,
    error,
    analyticsData,
    sectionStats,
    refreshData: fetchAnalyticsData
  };
};

/**
 * Provider component for analytics data
 */
export const AnalyticsProvider = ({ children }: { children: ReactNode }) => {
  const analyticsData = useAnalyticsDataFetcher();

  // Use createElement instead of JSX since this is a .ts file
  return createElement(
    AnalyticsContext.Provider,
    { value: analyticsData },
    children
  );
};

/**
 * Custom hook to use the analytics context
 */
export const useAnalyticsData = (): AnalyticsContextType => {
  const context = useContext(AnalyticsContext);

  if (!context) {
    throw new Error('useAnalyticsData must be used within an AnalyticsProvider');
  }

  return context;
};
